/**
 * «Аналитика»: список отчётов исследований (`/analytics`) и отчёт (`/analytics/:slug`) — вступление и вкладки
 * по разделам `##` (`lib/analyticsTabs`), вкладка в адресе `?tab=`. Markdown рисует `react-markdown` + `remark-gfm`
 * (таблицы); сырой HTML не исполняется. Ссылки наружу — в новой вкладке.
 */
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import Markdown, { type Components } from "react-markdown";
import { Link, useParams, useSearchParams } from "react-router";
import remarkGfm from "remark-gfm";

import { Empty, ErrorLine, Loading } from "@/components/trends/ui";
import { analyticsKeys, loadReport, loadReports, reportPath } from "@/lib/analytics";
import { splitTabs, tabIndex } from "@/lib/analyticsTabs";

const dateLabel = (iso: string) => {
  const d = new Date(iso + "T00:00:00");
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
};

export function AnalyticsPage() {
  const list = useQuery({ queryKey: analyticsKeys.list, queryFn: loadReports });
  return (
    <div className="grid max-w-[900px] gap-3 p-5" data-annot="аналитика">
      <div>
        <h1 className="text-[18px] font-semibold tracking-[-0.01em] text-fg">Аналитика</h1>
        <p className="mt-0.5 text-[12.5px] text-fg-muted">исследования для контента, новые сверху</p>
      </div>
      {list.isError ? <ErrorLine error={list.error} /> : null}
      {!list.data && !list.isError ? (
        <Loading />
      ) : list.data && list.data.reports.length === 0 ? (
        <Empty>Отчётов пока нет</Empty>
      ) : (
        <ul className="grid gap-2">
          {list.data?.reports.map((r) => (
            <li key={r.slug}>
              <Link
                to={reportPath(r.slug)}
                className="grid gap-0.5 rounded-[var(--radius-card)] border border-border bg-bg-subtle px-4 py-3 hover:border-border-strong"
              >
                <span className="text-[14px] font-medium text-fg">{r.title}</span>
                <span className="text-[12px] text-fg-muted">{dateLabel(r.date)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AnalyticsReportPage() {
  const { slug = "" } = useParams();
  const [search, setSearch] = useSearchParams();
  const one = useQuery({ queryKey: analyticsKeys.one(slug), queryFn: () => loadReport(slug) });
  const parts = one.data ? splitTabs(one.data.markdown) : null;
  const active = parts ? tabIndex(search.get("tab"), parts.tabs.length) : 0;

  return (
    <div className="grid max-w-[900px] gap-3 p-5" data-annot="отчёт аналитики">
      <div className="text-[12.5px]">
        <Link to="/analytics" className="text-accent-text hover:underline">
          ← Аналитика
        </Link>
      </div>
      {one.isError ? <ErrorLine error={one.error} /> : null}
      {!one.data && !one.isError ? <Loading /> : null}
      {one.data && parts ? (
        <>
          <div>
            <h1 className="text-[18px] font-semibold tracking-[-0.01em] text-fg">{parts.title ?? one.data.title}</h1>
            <p className="mt-0.5 text-[12.5px] text-fg-muted">{dateLabel(one.data.date)}</p>
          </div>
          {parts.intro ? <Md text={parts.intro} /> : null}
          {parts.tabs.length > 0 ? (
            <>
              <div className="flex flex-wrap gap-1 border-b border-border" role="tablist" data-annot="вкладки отчёта">
                {parts.tabs.map((t, i) => (
                  <button
                    key={t.title + i}
                    type="button"
                    role="tab"
                    aria-selected={i === active}
                    onClick={() => setSearch(i === 0 ? {} : { tab: String(i) }, { replace: true })}
                    className={clsx(
                      "-mb-px border-b-2 px-3 py-1.5 text-[13px]",
                      i === active ? "border-accent font-medium text-fg" : "border-transparent text-fg-muted hover:text-fg",
                    )}
                  >
                    {t.title}
                  </button>
                ))}
              </div>
              <Md text={parts.tabs[active]!.body} />
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** Оформление markdown без плагина типографики: те же размеры и цвета, что у остальной панели. */
const MD: Components = {
  h1: ({ children }) => <h2 className="mt-4 text-[16px] font-semibold text-fg">{children}</h2>,
  h2: ({ children }) => <h2 className="mt-4 text-[16px] font-semibold text-fg">{children}</h2>,
  h3: ({ children }) => <h3 className="mt-4 text-[14.5px] font-semibold text-fg">{children}</h3>,
  h4: ({ children }) => <h4 className="mt-3 text-[13.5px] font-semibold text-fg">{children}</h4>,
  p: ({ children }) => <p className="my-2">{children}</p>,
  ul: ({ children }) => <ul className="my-2 list-disc pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="my-2 list-decimal pl-5">{children}</ol>,
  li: ({ children }) => <li className="my-0.5">{children}</li>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer noopener" className="break-words text-accent-text hover:underline">
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 border-accent bg-bg-subtle px-3 py-1 text-fg">{children}</blockquote>
  ),
  code: ({ children }) => <code className="rounded-[3px] bg-bg-elevated px-1 text-[12.5px]">{children}</code>,
  pre: ({ children }) => (
    <pre className="my-2 overflow-x-auto whitespace-pre-wrap rounded-[var(--radius-ui)] bg-bg-elevated p-3 text-[12.5px]">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-[12.5px]">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border border-border bg-bg-subtle px-2 py-1 text-left font-medium">{children}</th>,
  td: ({ children }) => <td className="border border-border px-2 py-1 align-top">{children}</td>,
  hr: () => <hr className="my-4 border-border" />,
};

function Md({ text }: { text: string }) {
  return (
    <div className="text-[13.5px] leading-relaxed text-fg">
      <Markdown remarkPlugins={[remarkGfm]} components={MD}>
        {text}
      </Markdown>
    </div>
  );
}
